import React, { useEffect, useState } from "react";
import { API_ciudadanos } from "../../config/apiConfig";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "react-toastify";
import { FaUserGraduate, FaPenToSquare, FaCheck, FaX, FaCirclePlus, FaRegTrashCan } from "react-icons/fa6";
import "../../pages/user/css/Curriculum.css";

const Formacion = ({ usuario }) => {
  const [formaciones, setFormaciones] = useState([]);

  // Estados de edición
  const [editIndex, setEditIndex] = useState(null);
  const [tempFormacion, setTempFormacion] = useState({});
  const [isNewItem, setIsNewItem] = useState(false);

  // Opciones para el nivel de educación CINE
  const opcionesNivel = [
    "Cursos de formación no reglada o similar",
    "CINE0: Menos que primaria",
    "CINE1: Educación primaria",
    "CINE2: Primera etapa de educación secundaria y similar (E.S.O., Certificados de profesionalidad N1 y N2)",
    "CINE3: Segunda etapa de educación secundaria y similar (Bachillerato, FP Básica y de Grado Medio)",
    "CINE4: Educación postsecundaria no superior (Certificado de profesionalidad N3)",
    "CINE5: FP, artes plásticas y diseño, deportivas de grado superior y equivalentes; títulos propios universitarios ≥ 2 años",
    "CINE6: Grados universitarios de 240 ECTS, diplomados, títulos propios de experto o especialista, y similares",
    "CINE7: Grados universitarios de más de 240 ECTS, licenciados, másteres y especialidades en Ciencias de la Salud por residencia",
    "CINE8: Enseñanzas de doctorado",
    "Otros",
  ];

  useEffect(() => {
    if (usuario?.userId) {
      fetch(`${API_ciudadanos}/${usuario.userId}/curriculum`)
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data.EducationHistory) && data.EducationHistory.length > 0) {
            const mapped = data.EducationHistory.map((item, index) => ({
              id: String(index + 1),
              estudios: item.title || "",
              nivel: item.educationLevel || "Educación primaria",
              centro: item.institution || "",
              fechaInicio: item.startDate?.split("T")[0] || "",
              fechaFin: item.endDate?.split("T")[0] || "",
              tareas: item.description || "",
              actualidad: item.isOngoing || false,
            }));
            setFormaciones(mapped);
          } else {
            // Si no hay formación, inicializar con array vacío
            setFormaciones([]);
          }
        })
        .catch((err) => console.error("Error al cargar formación:", err));
    }
  }, [usuario]);

  const handleSaveEdit = async () => {
    const updatedFormaciones = [...formaciones];
    updatedFormaciones[editIndex] = tempFormacion;
    setFormaciones(updatedFormaciones);
    setEditIndex(null);
    setIsNewItem(false);

    try {
      const payload = {
        EducationHistory: updatedFormaciones.map((item) => ({
          title: item.estudios,
          educationLevel: item.nivel,
          institution: item.centro,
          startDate: item.fechaInicio,
          endDate: item.actualidad ? null : item.fechaFin,
          isOngoing: item.actualidad,
          description: item.tareas,
        })),
      };

      const res = await fetch(`${API_ciudadanos}/${usuario.userId}/curriculum`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Error al guardar formación");

      toast.success("Formación actualizada correctamente");
    } catch (error) {
      console.error("❌ Error al guardar formación:", error);
      toast.error("No se pudo guardar la formación");
    }
  };

  const formatFecha = (fechaISO) => {
    if (!fechaISO) return "";
    const [a, m, d] = fechaISO.split("T")[0].split("-");
    return `${d}/${m}/${a}`;
  };

  // Habilitar edición
  const handleEdit = (index) => {
    setEditIndex(index);
    setTempFormacion({ ...formaciones[index] });
    setIsNewItem(false);
  };

  // Opcion Actualidad, en vez de Fecha Fin
  const handleCheckboxChange = () => {
    setTempFormacion((prev) => ({
      ...prev,
      actualidad: !prev.actualidad,
      fechaFin: !prev.actualidad ? "En curso" : "",
    }));
  };

  // Cancelar edición
  const handleCancelEdit = () => {
    if (isNewItem) {
      // Si es un elemento nuevo, eliminarlo
      setFormaciones(formaciones.slice(1));
    }
    setEditIndex(null);
    setIsNewItem(false);
  };

  // Eliminar formación
  const handleDelete = async (index) => {
    const updatedFormaciones = formaciones.filter((_, i) => i !== index);
    setFormaciones(updatedFormaciones);

    // Preparamos los datos para enviar al backend
    const payload = {
      EducationHistory: updatedFormaciones.map((item) => ({
        title: item.estudios,
        educationLevel: item.nivel,
        institution: item.centro,
        startDate: item.fechaInicio,
        endDate: item.actualidad ? null : item.fechaFin,
        isOngoing: item.actualidad,
        description: item.tareas,
      })),
    };

    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_ciudadanos}/${usuario.userId}/curriculum`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Error al eliminar formación");

      toast.success("Formación eliminada correctamente");
    } catch (error) {
      console.error("❌ Error al eliminar formación:", error);
      toast.error("No se pudo eliminar la formación");
      // Revertir el cambio en caso de error
      setFormaciones(formaciones);
    }
  };

  // Añadir nueva formacion
  const handleAddFormacion = () => {
    const nuevaFormacion = {
      id: `${formaciones.length + 1}`,
      estudios: "",
      nivel: "Educación primaria",
      centro: "",
      fechaInicio: "",
      fechaFin: "",
      tareas: "",
      actualidad: false,
    };
    setFormaciones([nuevaFormacion, ...formaciones]);
    // Activar modo edición para el nuevo elemento
    setEditIndex(0);
    setTempFormacion({ ...nuevaFormacion });
    setIsNewItem(true);
  };

  // Función para manejar el drag & drop
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reorderedFormaciones = [...formaciones];
    const [movedItem] = reorderedFormaciones.splice(result.source.index, 1);
    reorderedFormaciones.splice(result.destination.index, 0, movedItem);
    setFormaciones(reorderedFormaciones);

    // Preparamos los datos para enviar al backend
    const payload = {
      EducationHistory: reorderedFormaciones.map((item) => ({
        title: item.estudios,
        educationLevel: item.nivel,
        institution: item.centro,
        startDate: item.fechaInicio,
        endDate: item.actualidad ? null : item.fechaFin,
        isOngoing: item.actualidad,
        description: item.tareas,
      })),
    };

    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_ciudadanos}/${usuario.userId}/curriculum`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Error al reordenar formaciones");

      toast.success("Orden de formaciones actualizado");
    } catch (error) {
      console.error("❌ Error al reordenar formaciones:", error);
      toast.error("No se pudo actualizar el orden de las formaciones");
      // Revertir el cambio en caso de error
      setFormaciones(formaciones);
    }
  };

  return (
    <div className="ocupaciones caja-verde">
      <div className="titulo-editar between blanco fondo-verde">
        <h2 className="blanco fondo-verde align-center mayusculas">
          <FaUserGraduate />
          Educación y Formación
        </h2>
        <button aria-label="Añadir formación" className="fondo-verde blanco no-border" onClick={handleAddFormacion}>
          <FaCirclePlus />
        </button>
      </div>

      {/* Drag & Drop Context */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="experiencias">
          {(provided) => (
            <div className="contenido column" {...provided.droppableProps} ref={provided.innerRef}>
              {formaciones.map((form, index) => (
                <Draggable key={form.id} draggableId={form.id} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="experiencia draggable"
                    >
                      <div className="between cada-experiencia">
                        <div className="column mb-10">
                          {/* Estudios */}
                          <h3 className={!form.estudios || form.estudios === "Estudios realizados" ? "texto-defecto" : ""}>
                            {editIndex === index ? (
                              <input
                                aria-label="Nombre de la formación"
                                className="w-450"
                                type="text"
                                placeholder="Estudios realizados"
                                value={tempFormacion.estudios}
                                onChange={(e) =>
                                  setTempFormacion({
                                    ...tempFormacion,
                                    estudios: e.target.value,
                                  })
                                }
                              />
                            ) : (
                              form.estudios || "Estudios realizados"
                            )}
                          </h3>
                          {/* Nivel CINE de estudios */}
                          {editIndex === index ? (
                            <select
                              aria-label="Nivel de la educación según la escala CINE"
                              value={tempFormacion.nivel}
                              onChange={(e) =>
                                setTempFormacion({
                                  ...tempFormacion,
                                  nivel: e.target.value,
                                })
                              }
                            >
                              {opcionesNivel.map((opcion) => (
                                <option key={opcion} value={opcion}>
                                  {opcion}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <p className={`nivel-cine ${!form.nivel || form.nivel === "Nivel de la educación" ? "texto-defecto" : ""}`}>
                              {form.nivel || "Nivel de la educación"}
                            </p>
                          )}
                        </div>
                        <div className="acciones acciones-experiencia flex">
                          {editIndex === index ? (
                            <>
                              <button aria-label="Guardar cambios" className="check fondo-blanco verde no-border" onClick={handleSaveEdit}>
                                <FaCheck />
                              </button>
                              <button
                                aria-label="Cancelar cambios"
                                className="check fondo-blanco rojo no-border"
                                onClick={handleCancelEdit}
                              >
                                <FaX />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                aria-label="Editar esta formación"
                                className="experiencia-edicion no-border editar-experiencia"
                                onClick={() => handleEdit(index)}
                              >
                                <FaPenToSquare />
                              </button>
                              <button
                                aria-label="Eliminar esta formación"
                                className="experiencia-edicion no-border eliminar-experiencia"
                                onClick={() => handleDelete(index)}
                              >
                                <FaRegTrashCan />
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="datos column gap-5">
                        {/* Centro de estudios */}
                        <p className={!form.centro || form.centro === "Nombre del centro de estudios" ? "texto-defecto" : ""}>
                          {editIndex === index ? (
                            <input
                              aria-label="Centro de estudios"
                              className="w-100"
                              type="text"
                              placeholder="Nombre del centro de estudios"
                              value={tempFormacion.centro}
                              onChange={(e) =>
                                setTempFormacion({
                                  ...tempFormacion,
                                  centro: e.target.value,
                                })
                              }
                            />
                          ) : (
                            form.centro || "Nombre del centro de estudios"
                          )}
                        </p>
                        {/* Fecha de inicio y fin */}
                        {editIndex === index ? (
                          <p className="align-center gap-10">
                            <input
                              aria-label="Fecha de inicio"
                              type="date"
                              value={tempFormacion.fechaInicio}
                              onChange={(e) =>
                                setTempFormacion({
                                  ...tempFormacion,
                                  fechaInicio: e.target.value,
                                })
                              }
                            />
                            {" - "}
                            <input
                              aria-label="Fecha fin"
                              type="date"
                              value={tempFormacion.fechaFin}
                              onChange={(e) =>
                                setTempFormacion({
                                  ...tempFormacion,
                                  fechaFin: e.target.value,
                                })
                              }
                              disabled={tempFormacion.actualidad}
                            />
                            <label className="align-center gap-5">
                              <input
                                aria-label="Sigo estudiando aquí"
                                type="checkbox"
                                checked={tempFormacion.actualidad}
                                onChange={handleCheckboxChange}
                              />{" "}
                              Actualidad
                            </label>
                          </p>
                        ) : (
                          <p className={`${form.fechaInicio === "" ? "texto-defecto" : ""}`}>{`${formatFecha(form.fechaInicio)} - ${
                            form.actualidad ? "En curso" : formatFecha(form.fechaFin)
                          }`}</p>
                        )}
                      </div>

                      {/* Breve descripción de tareas en un solo textarea */}
                      <div className="tareas">
                        {editIndex === index ? (
                          <textarea
                            aria-label="Breve descripción de la formación"
                            className="w-100"
                            placeholder="Breve descripción de la formación"
                            value={tempFormacion.tareas}
                            onChange={(e) =>
                              setTempFormacion({
                                ...tempFormacion,
                                tareas: e.target.value,
                              })
                            }
                          />
                        ) : (
                          <p
                            className={!form.tareas || form.tareas === "Breve descripción de tareas realizadas" ? "texto-defecto" : ""}
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {form.tareas || "Breve descripción de la formación"}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default Formacion;
